import { Network, type ConnectionStatus } from '@capacitor/network'
import type { PluginListenerHandle } from '@capacitor/core'
import {
  addHarnessNetworkListener,
  getHarnessNetworkStatus,
} from '@/lib/devtools/harness/domains/offline/capacitorNetworkTransport'

/**
 * Production-side wrappers around `Network.getStatus()` and
 * `Network.addListener('networkStatusChange', ...)`. Every call site
 * that needs to read or subscribe to the device's network status should
 * import from here, not directly from `@capacitor/network`.
 *
 * In dev builds with `VITE_ENABLE_DEV_HARNESS=true`, the wrappers
 * consult the harness:
 *   - `getStatus()` checks the harness override first; when the
 *     developer has flipped the NetworkPanel to "offline", every call
 *     site sees `{connected: false, connectionType: 'none'}` and
 *     triggers its existing offline-handling path.
 *   - `addListener()` subscribes to BOTH the harness's synthetic firings
 *     (so mode flips mid-session fire `networkStatusChange` callbacks)
 *     AND the real plugin (so genuine device events still flow). The
 *     returned handle's `remove()` tears down both subscriptions.
 *
 * In production builds, every `if (__DEV_HARNESS__) { ... }` branch is
 * dead code, the harness imports are tree-shaken away, and the
 * wrappers degenerate into direct calls to the real plugin.
 * `verify-prod-build` asserts the harness symbols don't appear in
 * `dist/`.
 *
 * Probe-cache invalidation is also wired through the harness (every
 * mode transition calls `invalidateProbeCache()` from inside
 * `capacitorNetworkTransport.ts`) — callers do not need to think about
 * it. The pre-existing manual invalidation in `useNativeQueueAutoSync`'s
 * networkStatusChange callback continues to work, just becomes
 * redundant when the harness is the event source.
 */
export async function getStatus(): Promise<ConnectionStatus> {
  if (__DEV_HARNESS__) {
    const override = getHarnessNetworkStatus()
    if (override) return override
  }
  return Network.getStatus()
}

export async function addListener(
  eventName: 'networkStatusChange',
  cb: (status: ConnectionStatus) => void,
): Promise<PluginListenerHandle> {
  if (__DEV_HARNESS__) {
    // Subscribe to BOTH sources so the callback fires for harness mode
    // flips AND real device events. The harness handle is local +
    // synchronous; the real plugin handle is awaited because Capacitor's
    // bridge round-trips through native code.
    //
    // Failure semantics: if the real plugin's `addListener` rejects, we
    // must tear the harness listener back down before re-throwing.
    // Otherwise the caller would receive a rejected promise (with no
    // handle to call `remove()` on) while the synthetic listener stays
    // alive in the registry forever — every future mode flip would fire
    // the orphaned callback. Match the all-or-nothing contract callers
    // already expect from the real plugin.
    const harnessHandle = addHarnessNetworkListener(cb)
    let realHandle: PluginListenerHandle
    try {
      realHandle = await Network.addListener(eventName, cb)
    } catch (error) {
      await harnessHandle.remove()
      throw error
    }
    return {
      remove: async () => {
        await harnessHandle.remove()
        await realHandle.remove()
      },
    }
  }
  return Network.addListener(eventName, cb)
}
